import React, { useEffect, useCallback, useState, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import GamePage from './components/GamePage';
import LeaderBoard from './components/LeaderBoard';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Impressum from './components/Impressum';
import DeletionStatus from './components/DeletionStatus';
import DeleteAccount from './components/DeleteAccount';
import HomeContent from './components/HomeContent';
import Navbar from './components/NavigationBar';
import TutorialModal from './components/TutorialModal';
import UsernameSelectionModal from './components/UsernameSelectionModal';
import FeedbackModal from './components/FeedbackModal';
import { AdBannerLayout } from './components/ads/AdBanner';
import CookieConsent, { reopenCookieSettings } from './components/CookieConsent';
import { AuthProvider } from './contexts/AuthContext';
import { DarkModeProvider, useDarkMode } from './contexts/DarkModeContext';
import { LanguageProvider, useTranslation } from './contexts/LanguageContext';
import LanguageAlternates from './components/LanguageAlternates';
import { basenameFor, localizedPath } from './i18n/languages';
import { MonetizationProvider } from './contexts/MonetizationContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { PreferencesProvider, usePreferences } from './contexts/PreferencesContext';
import { isNative } from './utils/native';
import { trackPageview } from './utils/analytics';
import { reloadOnNewBuild } from './utils/freshBuild';
import { t as translate } from './i18n';
import './css/App.css';
import './css/GamePage.css';
import './css/LeaderBoard.css';
import './css/auth.css';

const TUTORIAL_SEEN_KEY = 'wordlabyrinth_tutorial_seen';

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error, info) {
        console.error('App error:', error, info);
        if (process.env.REACT_APP_SENTRY_DSN) {
            // Lazy import keeps Sentry out of the path when unconfigured.
            import('@sentry/react').then(Sentry => {
                Sentry.withScope(scope => {
                    scope.setExtras({ componentStack: info?.componentStack });
                    Sentry.captureException(error);
                });
            }).catch(() => {});
        }
    }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#ccc' }}>
                    <h2>{translate('common.somethingWentWrong')}</h2>
                    <button onClick={() => window.location.reload()}
                        style={{ padding: '0.5rem 1.5rem', marginTop: '1rem', cursor: 'pointer' }}>
                        {translate('common.reload')}
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

function SiteFooter() {
    const { t } = useTranslation();
    const location = useLocation();
    const [emailCopied, setEmailCopied] = useState(false);
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === 'undefined') return false;
        return isNative() || window.innerWidth <= 768;
    });

    useEffect(() => {
        const onResize = () => setIsMobile(isNative() || window.innerWidth <= 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Hide footer on legal pages (they already show the company info)
    if (['/privacy', '/terms', '/impressum'].includes(location.pathname)) return null;

    // Mobile apps / small screens: legal links live in the navbar options
    // dropdown and Settings modal (industry standard). No footer in the
    // document flow prevents white-bar flicker when the keyboard opens.
    if (isMobile) return null;

    // On native mobile, mailto: links may not work in WKWebView (no mail app).
    // Copy email to clipboard and briefly show confirmation.
    const handleMailtoClick = (e) => {
        if (window.Capacitor?.isNativePlatform?.()) {
            e.preventDefault();
            const email = 'support@word-labyrinth.com';
            navigator.clipboard?.writeText?.(email)
                .then(() => { setEmailCopied(true); setTimeout(() => setEmailCopied(false), 2000); })
                .catch(() => alert(t('nav.contactUsAt', { email })));
        }
    };

    return (
        <footer className="site-footer">
            <div className="footer-links">
                <a href="/how-to-play/">{t('nav.footer.howToPlay')}</a>
                <span className="footer-sep">·</span>
                <a href="/word-ladder/">{t('nav.footer.wordLadder')}</a>
                <span className="footer-sep">·</span>
                <a href="/puzzles/">{t('nav.footer.puzzleArchive')}</a>
                <span className="footer-sep">·</span>
                <a href="/blog/">{t('nav.blog')}</a>
                <span className="footer-sep">·</span>
                <a href="/faq/">{t('nav.faq')}</a>
                <span className="footer-sep">·</span>
                <a href="/about/">{t('nav.about')}</a>
            </div>
            <div className="footer-links">
                <Link to="/terms">{t('nav.footer.terms')}</Link>
                <span className="footer-sep">·</span>
                <Link to="/privacy">{t('nav.footer.privacy')}</Link>
                <span className="footer-sep">·</span>
                <Link to="/impressum">{t('nav.legalNotice')}</Link>
                <span className="footer-sep">·</span>
                <button className="footer-link-btn" onClick={reopenCookieSettings}>{t('nav.footer.cookies')}</button>
                <span className="footer-sep">·</span>
                <a href="mailto:support@word-labyrinth.com" onClick={handleMailtoClick}>
                    {emailCopied ? t('nav.footer.emailCopied') : t('nav.contact')}
                </a>
                <span className="footer-sep">·</span>
                <span className="footer-company">© {new Date().getFullYear()} CoreDL d.o.o.</span>
            </div>
        </footer>
    );
}

function MobileAuthGate({ children }) {
    // Native apps now allow guest play, like the web. This previously showed a
    // hard login wall to every unauthenticated native user — a major growth
    // blocker (≈0 native users ever registered because they had to sign up
    // before trying the game). Sign-in stays available in-app (nav / settings)
    // and via the soft post-first-win prompt (B15). Kept as a thin wrapper so
    // the route element does not need to change.
    return children;
}

// HomeContent stays hook-free for the prerender, so the language is read here.
function LocalizedHomeContent() {
    const { language } = useTranslation();
    return <HomeContent lang={language} />;
}

function AppRoutes() {
    const location = useLocation();
    const isLegalPage = ['/privacy', '/terms', '/impressum'].includes(location.pathname);
    const { preferences, updatePreference } = usePreferences();

    useEffect(() => reloadOnNewBuild(), []);

    // Track SPA pageviews with Plausible
    useEffect(() => {
        trackPageview(window.location.href);
    }, [location.pathname]);

    // First-run guidance is handled by the interactive onboarding flow in
    // GamePage (see docs/ONBOARDING_REDESIGN.md), not this passive modal. The
    // modal stays available on demand via the navbar "How to play" button.
    const [showTutorial, setShowTutorial] = useState(false);

    // Hide tutorial if server says it was already seen on another device
    useEffect(() => {
        if (preferences.tutorialSeen && showTutorial) {
            setShowTutorial(false);
        }
    }, [preferences.tutorialSeen]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCloseTutorial = useCallback(() => {
        setShowTutorial(false);
        localStorage.setItem(TUTORIAL_SEEN_KEY, 'true');
        updatePreference('tutorialSeen', true);
    }, [updatePreference]);

    const handleOpenTutorial = useCallback(() => {
        setShowTutorial(true);
    }, []);

    return (
        <>
            <LanguageAlternates />
            <Navbar onHelpClick={handleOpenTutorial} />
            <AdBannerLayout footer={<SiteFooter />}>
                <main className="main-content">
                    <Routes>
                        <Route path="/" element={<GamePage />} />
                        <Route path="/leaderboard" element={<LeaderBoard />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/impressum" element={<Impressum />} />
                        <Route path="/deletion-status" element={<DeletionStatus />} />
                        <Route path="/delete-account" element={<DeleteAccount />} />
                    </Routes>
                    {/* Crawlable SEO content below the game (web home only).
                        Gives JS-rendering crawlers real text content instead of
                        a near-empty game shell. Never rendered in native apps. */}
                    {location.pathname === '/' && !isNative() && <LocalizedHomeContent />}
                </main>
            </AdBannerLayout>
            {!isLegalPage && <TutorialModal show={showTutorial} onClose={handleCloseTutorial} />}
            <CookieConsent />
            <FeedbackModal />
            <UsernameSelectionModal />
        </>
    );
}

function AppContent() {
    const { isDarkMode } = useDarkMode();
    const { language } = useTranslation();

    useEffect(() => {
        const currentTheme = isDarkMode ? 'dark' : 'light';
        document.documentElement.setAttribute('data-theme', currentTheme);
        localStorage.setItem('theme', currentTheme);
    }, [isDarkMode]);

    // The address always names the active language (/hr/… for Croatian); switching remounts the routes under it.
    const basename = basenameFor(language);
    const wanted = localizedPath(window.location.pathname, language);
    if (wanted !== window.location.pathname) {
        window.history.replaceState(window.history.state, '', wanted + window.location.search + window.location.hash);
    }

    return (
        <div className={`App ${isDarkMode ? 'dark-mode' : ''}`}>
            <Router basename={basename || undefined} key={basename || '/'}>
                <Routes>
                    <Route path="/*" element={<MobileAuthGate><AppRoutes /></MobileAuthGate>} />
                </Routes>
            </Router>
        </div>
    );
}

function App() {
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

    return (
        <HelmetProvider>
            <GoogleOAuthProvider clientId={googleClientId}>
                <ErrorBoundary>
                <AuthProvider>
                    <PreferencesProvider>
                        <DarkModeProvider>
                            <NotificationProvider>
                                <MonetizationProvider>
                                    <LanguageProvider>
                                        <AppContent />
                                    </LanguageProvider>
                                </MonetizationProvider>
                            </NotificationProvider>
                        </DarkModeProvider>
                    </PreferencesProvider>
                </AuthProvider>
                </ErrorBoundary>
            </GoogleOAuthProvider>
        </HelmetProvider>
    );
}

export default App;
